import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useToast } from '../../components/Toast.js';
import { Dialog, Notice } from '../../components/ui.js';
import { useOffline } from '../../offline/context.js';
import { useSignedIn } from '../../session.js';
import { todoItems } from '../leads/data.js';
import { ReviewCall, type ReviewItem } from './ReviewCall.js';

/**
 * The review call's route (/todo/voice, or /todo/voice/<kind>/<id> for one
 * record from its sheet or row): everything to document, or that record;
 * at the end, back where it was opened, with what is left in To do.
 */
export function ReviewCallWindow() {
  const { kind, id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { user, can } = useSignedIn();
  const { store } = useOffline();
  const [queue, setQueue] = useState<ReviewItem[] | null>(
    kind && id && (kind === 'lead' || kind === 'contact') ? [{ kind, id }] : null,
  );
  const from = (location.state as { from?: string } | null)?.from ?? '/todo';

  useEffect(() => {
    if (queue || !store) return;
    let current = true;
    void todoItems(store, user.id).then(
      (items) =>
        current &&
        // Contacts first, as the prototype: a contact completed can become a lead.
        setQueue(
          [
            ...items.filter((i) => i.kind === 'contact'),
            ...items.filter((i) => i.kind === 'lead'),
          ].map((i) => ({ kind: i.kind, id: i.id })),
        ),
    );
    return () => {
      current = false;
    };
  }, [queue, store, user.id]);

  const close = () => navigate(from, { replace: true });
  if (!can('todo.voice')) {
    return (
      <Dialog small title="Review call" onClose={close}>
        <Notice kind="error">You are not allowed to complete by voice.</Notice>
      </Dialog>
    );
  }
  if (!queue) {
    return (
      <Dialog small title="Review call" onClose={close}>
        <div className="spinner" aria-label="Loading" />
      </Dialog>
    );
  }
  return (
    <ReviewCall
      queue={queue}
      onEnd={(filled) => {
        if (filled > 0) toast('Saved as you went. Here is what is left.');
        close();
      }}
    />
  );
}
